/**
 * Themes & sentiment.
 *
 * Every surface on this screen depends on call_topics and calls.sentiment_label.
 * Neither exists before migration 002 is applied and the analysis step written,
 * so the screen leads with what is missing rather than rendering an empty chart
 * frame that looks like a measurement of zero.
 *
 * The topic cloud stays a supporting view, as labelled in the design. It is sized
 * by real volume; the theme notes ("Callers chasing updates between exchange and
 * completion") are editorial and are not invented here.
 */
import { c, radius } from '../theme/tokens';
import { Card, CardHead, Eyebrow, H1, H3, Mono } from '../components/primitives';
import { Movement, SentimentBars, ThemeTrack } from '../components/charts';
import { Failure, LoadingPanel, NotReady } from '../components/states';
import { SelectGapsStrip } from '../components/LockedStrip';
import type { DashboardContext, SentimentResult, ThemesResult } from '../api/types';

export function Themes({
  ctx,
  themes,
  sentiment,
  loading,
  error,
  onPickTopic,
  onOpenCall,
  onOpenSentimentCalls,
  onRetry,
  onUpgrade,
}: {
  ctx: DashboardContext;
  themes: ThemesResult | null;
  sentiment: SentimentResult | null;
  loading: boolean;
  error: string | null;
  onPickTopic: (topic: string) => void;
  onOpenCall: (id: string) => void;
  onOpenSentimentCalls: (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => void;
  onRetry: () => void;
  onUpgrade: (tier: 'pro' | 'select') => void;
}) {
  const selectUnlocked =
    ctx.capabilities.cap_staff_routing && ctx.capabilities.cap_document_access && ctx.capabilities.cap_omniprop_intel;

  const maxVolume = Math.max(...(themes?.rows.map(t => t.volume) ?? [1]), 1);
  const selected = themes?.topic ?? null;
  const selectedRow = themes?.rows.find(t => t.name === selected) ?? null;
  const maxWeight = Math.max(...(themes?.cloud.map(w => w.weight) ?? [1]), 1);

  return (
    <div
      style={{
        padding: '44px 40px 64px 40px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>THEMES &amp; SENTIMENT · LAST 7 DAYS</Eyebrow>
          <H1>What {ctx.branch.name} is calling about</H1>
        </div>
        {themes?.ready ? (
          <Mono size={11} tracking={0.08}>
            {themes.analysed} {themes.analysed === 1 ? 'CALL' : 'CALLS'} ANALYSED
          </Mono>
        ) : null}
      </div>

      {error ? <Failure message={error} onRetry={onRetry} /> : null}
      {loading && !themes ? <LoadingPanel label="LOADING THEMES" /> : null}

      {themes && !themes.ready ? (
        <NotReady
          title="Nothing has been grouped into themes yet."
          body="Themes come from normalised topics written per call at the call_analyzed webhook event. That handler is a TODO in src/api/webhook.ts, and call_topics arrives with migration 002. Once both are in place this screen fills itself from live calls — no configuration needed."
          objects={themes.notReady}
        />
      ) : null}

      {themes?.ready ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 24, alignItems: 'start' }}>
          {/* Top five themes */}
          <Card padding={28} gap={22}>
            <CardHead
              titleSize={19}
              title="Top five themes"
              right={
                <Mono size={10.5} tracking={0.1}>
                  VS PREVIOUS 7 DAYS
                </Mono>
              }
            />
            {themes.rows.length === 0 ? (
              <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
                No topics recorded in the last seven days.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {themes.rows.slice(0, 5).map((t, i) => {
                  const active = t.name === selected;
                  return (
                    <button
                      key={t.name}
                      className="vp-reset"
                      onClick={() => onPickTopic(t.name)}
                      style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 0', width: '100%' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, width: '100%' }}>
                        <Mono size={11}>{String(i + 1).padStart(2, '0')}</Mono>
                        <span style={{ flex: 1, textAlign: 'left', fontSize: 17, color: active ? c.indigo : c.ink }}>
                          {t.name}
                        </span>
                        <span style={{ fontSize: 15, color: c.ink }}>
                          {t.volume} {t.volume === 1 ? 'call' : 'calls'}
                        </span>
                        <Movement movement={t.movement} size={11.5} width={44} />
                      </div>
                      <ThemeTrack pct={(t.volume / maxVolume) * 100} height={8} fill={active ? c.productInk : c.indigo} />
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
            <Card padding={28} gap={18}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <H3 size={19}>{selected ?? 'No theme selected'}</H3>
                {selectedRow ? (
                  <span style={{ fontSize: 13.5, color: c.ink3 }}>
                    {selectedRow.volume} {selectedRow.volume === 1 ? 'call' : 'calls'} this week ·{' '}
                    {selectedRow.movement == null
                      ? 'no comparison'
                      : selectedRow.movement === 0
                        ? 'level on last week'
                        : selectedRow.movement > 0
                          ? `up ${selectedRow.movement} on last week`
                          : `down ${Math.abs(selectedRow.movement)} on last week`}
                  </span>
                ) : null}
              </div>

              {(themes.examples ?? []).length === 0 ? (
                <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
                  No example calls for this theme in the range.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(themes.examples ?? []).map(ex => (
                    <button
                      key={ex.callId}
                      className="vp-reset vp-example"
                      onClick={() => onOpenCall(ex.callId)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                        padding: '12px 14px',
                        border: `1px solid ${c.line}`,
                        borderRadius: radius.button,
                        width: '100%',
                      }}
                    >
                      <Mono size={10.5}>{ex.meta}</Mono>
                      <span style={{ fontSize: 14.5, lineHeight: 1.5, color: c.ink, textAlign: 'left' }}>{ex.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card padding={28} gap={18}>
              <CardHead
                titleSize={17}
                title="Topic cloud"
                right={
                  <Mono size={10} tracking={0.1}>
                    SUPPORTING VIEW
                  </Mono>
                }
              />
              {themes.cloud.length === 0 ? (
                <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>Nothing to show yet.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', alignItems: 'baseline' }}>
                  {themes.cloud.map(w => {
                    const weight = (w.weight / maxWeight) * 30;
                    return (
                      <button
                        key={w.word}
                        className="vp-reset vp-cloud"
                        onClick={() => onPickTopic(w.word)}
                        style={{
                          fontSize: Math.round(12 + weight * 0.62),
                          color: weight > 20 ? c.indigo : weight > 15 ? c.lapis : c.ink3,
                          lineHeight: 1.2,
                        }}
                      >
                        {w.word}
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      ) : null}

      {/* Sentiment panel */}
      <Card padding={28} gap={24}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <H3 size={19}>Sentiment, and what is driving it</H3>
            <span style={{ fontSize: 13, color: c.ink3 }}>Positive · neutral · frustrated, by day</span>
          </div>
          {sentiment?.summary ? (
            <div style={{ display: 'flex', gap: 28 }}>
              {[
                { label: 'POSITIVE', value: sentiment.summary.positive, colour: c.success },
                { label: 'NEUTRAL', value: sentiment.summary.neutral, colour: c.ink3 },
                { label: 'FRUSTRATED', value: sentiment.summary.frustrated, colour: c.danger },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <Mono size={10} tracking={0.12}>
                    {s.label}
                  </Mono>
                  <span style={{ fontSize: 26, lineHeight: 1, color: s.colour }}>{s.value}%</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {!sentiment ? (
          <LoadingPanel label="LOADING SENTIMENT" />
        ) : !sentiment.ready ? (
          <NotReady
            compact
            title="Sentiment is not recorded on calls yet."
            body="Each call needs a sentiment label written when its transcript is analysed. Until then there is no basis for a positive-or-frustrated split, and none is shown."
            objects={sentiment.notReady}
          />
        ) : sentiment.days.length === 0 ? (
          <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
            No analysed calls in the last seven days.
          </div>
        ) : (
          <>
            <SentimentBars days={sentiment.days} height={150} barHeight={118} gap={18} maxWidth={96} labelSize={11} onSegmentClick={onOpenSentimentCalls} />
            <Mono size={10} tracking={0.1} color={c.ink3}>TAP A COLOUR TO VIEW THE CALLS BEHIND IT</Mono>
            <div style={{ paddingTop: 6, borderTop: `1px solid ${c.lineSoft}` }}>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: c.ink3, paddingTop: 20, maxWidth: '78ch' }}>
                What is driving each band is a written conclusion, not an aggregate. It belongs to the analysis step, so
                it is left out rather than generated from the chart above.
              </div>
            </div>
          </>
        )}
      </Card>

      {!selectUnlocked ? <SelectGapsStrip onOpen={() => onUpgrade('select')} /> : null}
    </div>
  );
}
